import React, { useState, useEffect } from 'react';
import { marketplaceAPI } from '../../../services/marketplaceAPI';
import CreateListingModal from '../../../components/marketplace/CreateListingModal';
import StoreSettingsModal from '../../../components/marketplace/StoreSettingsModal';
import ConfirmationModal from '../../../components/marketplace/ConfirmationModal';
import { Plus, Package, DollarSign, TrendingUp, Edit, Trash2, Search, Settings, ClipboardList, AlertTriangle, CheckCircle, Truck, XCircle, Clock } from 'lucide-react';
import '../../../styles/marketplace.css';

const ProviderMarketplace = ({ user }) => {
    const [listings, setListings] = useState([]);
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('listings'); // 'listings' | 'orders'
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alert, setAlert] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);

    // Delete Confirmation State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    const [storeCurrency, setStoreCurrency] = useState('SAR');

    useEffect(() => {
        fetchProviderDetails();
        fetchListings();
        fetchOrders();
    }, []);

    const fetchProviderDetails = async () => {
        try {
            const res = await marketplaceAPI.getProviderDetails();
            if (res.status === 'success') {
                setStoreCurrency(res.data.currency || 'SAR');
            }
        } catch (error) {
            console.error("Error fetching provider details:", error);
        }
    };

    const fetchListings = async () => {
        // setLoading(true); // Don't block whole UI if just refreshing
        try {
            const response = await marketplaceAPI.getProviderListings();
            if (response.status === 'success') {
                setListings(response.data);
            }
        } catch (error) {
            console.error("Error fetching listings:", error);
        }
    };

    const fetchOrders = async () => {
        try {
            const response = await marketplaceAPI.getProviderOrders();
            if (response.status === 'success') {
                setOrders(response.data);
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateOrderStatus = async (orderId, newStatus) => {
        try {
            const response = await marketplaceAPI.updateOrderStatus(orderId, newStatus);
            if (response.status === 'success') {
                setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
                setAlert({ type: 'success', message: `Order ${orderId} updated to ${newStatus}` });
                setTimeout(() => setAlert(null), 3000);
            }
        } catch (error) {
            console.error("Error updating order:", error);
            setAlert({ type: 'error', message: "Failed to update status" });
        }
    };

    const handleSaveProduct = async (formData) => {
        setIsSubmitting(true);
        // Ensure currency matches store currency
        const productData = { ...formData, currency: storeCurrency };

        try {
            if (editingProduct) {
                // Update existing
                const response = await marketplaceAPI.updateProduct(editingProduct.id, productData);
                if (response.status === 'success') {
                    setListings(prev => prev.map(item => item.id === editingProduct.id ? response.data : item));
                    setAlert({ type: 'success', message: 'Product updated successfully!' });
                }
            } else {
                // Create new
                const response = await marketplaceAPI.createProduct(productData);
                if (response.status === 'success') {
                    setListings(prev => [response.data, ...prev]);
                    setAlert({ type: 'success', message: 'Product created successfully!' });
                }
            }
            setIsModalOpen(false);
            setEditingProduct(null); // Reset
            setTimeout(() => setAlert(null), 3000);
        } catch (error) {
            console.error("Error saving product:", error);
            setAlert({ type: 'error', message: 'Failed to save product.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ... (rest of code)

    const handleEdit = (product) => {
        setEditingProduct(product);
        setIsModalOpen(true);
    };

    const handleOpenCreate = () => {
        setEditingProduct(null);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!productToDelete) return;

        try {
            const response = await marketplaceAPI.deleteProduct(productToDelete.id);
            if (response.status === 'success') {
                setListings(prev => prev.filter(item => item.id !== productToDelete.id));
                setAlert({ type: 'success', message: 'Product deleted.' });
                setTimeout(() => setAlert(null), 3000);
            }
        } catch (error) {
            console.error("Error deleting product:", error);
            setAlert({ type: 'error', message: 'Failed to delete product.' });
        } finally {
            setIsDeleteModalOpen(false);
            setProductToDelete(null);
        }
    };

    // Calculate Stats
    const totalSales = orders
        .filter(o => o.status !== 'Cancelled')
        .reduce((sum, o) => sum + o.total, 0);

    const stats = [
        { label: 'Total Listings', value: listings.length, icon: Package, colorClass: 'blue' },
        { label: 'Total Sales', value: `${totalSales.toFixed(2)} ${storeCurrency}`, icon: DollarSign, colorClass: 'green' },
        { label: 'Views', value: listings.reduce((acc, item) => acc + (item.reviews * 15), 0), icon: TrendingUp, colorClass: 'purple' }, // Mock views based on reviews
    ];

    return (
        <div className="marketplace-root provider-theme">
            <div className="marketplace-container">

                {/* Alert Toast */}
                {alert && (
                    <div style={{
                        position: 'fixed', top: '100px', right: '30px', padding: '16px', borderRadius: '12px', zIndex: 100,
                        background: alert.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                        color: alert.type === 'success' ? '#166534' : '#991B1B',
                        border: `1px solid ${alert.type === 'success' ? '#DCFCE7' : '#FECACA'}`,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        {alert.message}
                    </div>
                )}

                {/* Header */}
                <div style={{ paddingTop: '32px', marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 className="marketplace-title">Seller Dashboard</h1>
                        <p className="marketplace-subtitle">Manage your shop, orders, and products</p>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            onClick={() => setIsSettingsOpen(true)}
                            className="mp-btn-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '12px', border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer' }}
                        >
                            <Settings size={20} color="#4B5563" />
                            Store Settings
                        </button>
                        <button
                            onClick={handleOpenCreate}
                            className="mp-btn-primary"
                        >
                            <Plus size={20} />
                            Add New Product
                        </button>
                    </div>
                </div>

                {/* Low Stock Alert */}
                {listings.filter(i => i.stock < 5 && i.stock > 0).length > 0 && (
                    <div style={{
                        background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '12px', padding: '16px',
                        marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', color: '#92400E'
                    }}>
                        <AlertTriangle size={24} color="#D97706" />
                        <div>
                            <span style={{ fontWeight: '700' }}>Low Stock Alert:</span> Some of your products are running low.
                            Resupply soon to avoid missing sales!
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="mp-stats-container">
                    {stats.map((stat, i) => (
                        <div key={i} className="mp-stat-card">
                            <div className={`mp-stat-icon ${stat.colorClass}`}>
                                <stat.icon size={24} />
                            </div>
                            <div>
                                <p style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>{stat.label}</p>
                                <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937', margin: 0 }}>{stat.value}</h3>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '24px', borderBottom: '1px solid #E5E7EB', marginBottom: '24px' }}>
                    <button
                        onClick={() => setActiveTab('listings')}
                        style={{
                            padding: '12px 4px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'listings' ? '2px solid var(--mp-primary)' : '2px solid transparent',
                            color: activeTab === 'listings' ? 'var(--mp-primary)' : '#6B7280',
                            fontWeight: '600',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Package size={20} />
                        Listings
                    </button>
                    <button
                        onClick={() => setActiveTab('orders')}
                        style={{
                            padding: '12px 4px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'orders' ? '2px solid var(--mp-primary)' : '2px solid transparent',
                            color: activeTab === 'orders' ? 'var(--mp-primary)' : '#6B7280',
                            fontWeight: '600',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <ClipboardList size={20} />
                        Orders
                        {orders.filter(o => o.status === 'Pending' || o.status === 'Processing').length > 0 && (
                            <span style={{
                                background: '#EF4444', color: 'white', fontSize: '12px', padding: '2px 6px',
                                borderRadius: '10px', marginLeft: '4px'
                            }}>
                                {orders.filter(o => o.status === 'Pending' || o.status === 'Processing').length}
                            </span>
                        )}
                    </button>
                </div>

                {activeTab === 'listings' ? (
                    <div className="mp-table-container">
                        <div style={{ padding: '24px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', margin: 0 }}>Your Listings</h2>
                            <div className="mp-search-wrapper" style={{ maxWidth: '300px' }}>
                                <input
                                    type="text"
                                    placeholder="Search listings..."
                                    className="mp-search-input"
                                />
                                <Search className="mp-search-icon" size={16} />
                            </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table className="mp-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Category</th>
                                        <th>Price</th>
                                        <th style={{ textAlign: 'center' }}>Stock</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading listings...</td>
                                        </tr>
                                    ) : listings.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>No listings found. Start selling today!</td>
                                        </tr>
                                    ) : (
                                        listings.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <img
                                                            src={item.image}
                                                            alt={item.title}
                                                            style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', background: '#F3F4F6' }}
                                                        />
                                                        <span style={{ fontWeight: '500', color: '#111827' }}>{item.title}</span>
                                                    </div>
                                                </td>
                                                <td style={{ textTransform: 'capitalize', color: '#6B7280' }}>
                                                    {item.category}
                                                </td>
                                                <td style={{ fontWeight: '700', color: '#111827' }}>
                                                    {item.price} {storeCurrency}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {item.stock === 0 ? (
                                                        <span className="mp-status-badge inactive">Out of Stock</span>
                                                    ) : item.stock < 5 ? (
                                                        <span className="mp-status-badge" style={{ background: '#FEF3C7', color: '#D97706' }}>Low ({item.stock})</span>
                                                    ) : (
                                                        <span className="mp-status-badge active">{item.stock} in stock</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            style={{ padding: '8px', borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF' }}
                                                            title="Edit"
                                                        >
                                                            <Edit size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(item)}
                                                            style={{ padding: '8px', borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#EF4444' }}
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    /* Orders Table */
                    <div className="mp-table-container">
                        <div style={{ padding: '24px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', margin: 0 }}>Recent Orders</h2>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>
                                    Pending: <b>{orders.filter(o => o.status === 'Pending').length}</b>
                                </span>
                            </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table className="mp-table">
                                <thead>
                                    <tr>
                                        <th>Order ID</th>
                                        <th>Date</th>
                                        <th>Customer</th>
                                        <th>Items</th>
                                        <th>Total</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length === 0 ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No orders yet.</td></tr>
                                    ) : (
                                        orders.map(order => (
                                            <tr key={order.id}>
                                                <td style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#6B7280' }}>{order.id}</td>
                                                <td>{order.date}</td>
                                                <td>{order.customer}</td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        {order.items.map((item, idx) => (
                                                            <div key={idx} style={{ fontSize: '13px' }}>
                                                                {item.quantity}x {item.title}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td style={{ fontWeight: 'bold' }}>{order.total.toFixed(2)} {storeCurrency}</td>
                                                <td>
                                                    <span style={{
                                                        padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                                        background:
                                                            order.status === 'Delivered' ? '#D1FAE5' :
                                                                order.status === 'Shipped' ? '#DBEAFE' :
                                                                    order.status === 'Cancelled' ? '#FEE2E2' : '#FEF3C7',
                                                        color:
                                                            order.status === 'Delivered' ? '#047857' :
                                                                order.status === 'Shipped' ? '#1E40AF' :
                                                                    order.status === 'Cancelled' ? '#B91C1C' : '#D97706'
                                                    }}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {order.status !== 'Delivered' && order.status !== 'Cancelled' && (
                                                        <select
                                                            value={order.status}
                                                            onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                                                            style={{
                                                                padding: '6px', borderRadius: '6px', border: '1px solid #D1D5DB',
                                                                fontSize: '13px', cursor: 'pointer'
                                                            }}
                                                        >
                                                            <option value="Pending">Pending</option>
                                                            <option value="Processing">Processing</option>
                                                            <option value="Shipped">Shipped</option>
                                                            <option value="Delivered">Delivered</option>
                                                        </select>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <CreateListingModal
                    isOpen={isModalOpen}
                    onClose={() => { setIsModalOpen(false); setEditingProduct(null); }}
                    onSubmit={handleSaveProduct}
                    isSubmitting={isSubmitting}
                    productToEdit={editingProduct}
                    defaultCurrency={storeCurrency}
                />

                <StoreSettingsModal
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    onUpdate={(data) => {
                        if (data.currency) setStoreCurrency(data.currency);
                        setAlert({ type: 'success', message: 'Store profile updated!' });
                        setTimeout(() => setAlert(null), 3000);
                    }}
                />

                <ConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDelete}
                    title="Delete Product"
                    message={productToDelete ? `Are you sure you want to delete "${productToDelete.title}"? This action cannot be undone.` : "Are you sure?"}
                    confirmText="Delete"
                    isDestructive={true}
                />
            </div>
        </div>
    );
};

export default ProviderMarketplace;
